import SiteShell from "@/components/site/SiteShell";
import { SeoHead } from "@/components/seo/SeoHead";
import { useLocale } from "@/locale/LocaleProvider";
import { pickOfferSite, type OfferCard } from "@/locale/content/offerSite";
import { SITE_TELEGRAM_URL } from "@/lib/siteContact";
import { trackContactClick } from "@/lib/analytics";
import { ArrowRight, Shield, Zap, FileSearch, Layers } from "lucide-react";
import { LocalizedLink } from "@/components/LocalizedLink";

const OFFER_ICONS = {
  incident: Zap,
  audit: FileSearch,
  retainer: Shield,
  projects: Layers,
} as const;

function TgButton({ label, className = "" }: { label: string; className?: string }) {
  return (
    <a
      href={SITE_TELEGRAM_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackContactClick("telegram")}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[var(--a-primary)] px-6 py-3 text-sm font-semibold text-[var(--a-on-primary)] transition duration-200 hover:bg-[var(--a-primary-hover)] ${className}`}
    >
      {label}
      <ArrowRight className="h-4 w-4" aria-hidden />
    </a>
  );
}

function OfferTile({
  offer,
  delay,
  openLabel,
}: {
  offer: OfferCard;
  delay: number;
  openLabel: string;
}) {
  const Icon = OFFER_ICONS[offer.id];
  return (
    <LocalizedLink
      to={offer.href}
      className={`reveal reveal-delay-${delay} group flex flex-col rounded-2xl border border-[var(--a-border)] bg-[var(--a-card)] p-6 transition duration-200 hover:-translate-y-0.5 hover:border-[var(--a-primary)]/50 hover:shadow-[0_20px_40px_-24px_rgba(245,158,11,0.35)] sm:p-7`}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--a-accent-soft)] text-[var(--a-primary)]">
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <span className="text-right font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--a-primary)]">
          {offer.price}
        </span>
      </div>
      <h3 className="font-display text-xl font-semibold tracking-tight text-[var(--a-fg)] transition group-hover:text-[var(--a-primary-hover)]">
        {offer.title}
      </h3>
      <p className="mt-2 flex-1 text-[15px] leading-snug text-[var(--a-muted)]">{offer.blurb}</p>
      <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[var(--a-primary)]">
        {openLabel}
        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden />
      </span>
    </LocalizedLink>
  );
}

export default function Index() {
  const { lang } = useLocale();
  const c = pickOfferSite(lang);
  const openLabel = c.ui.open;

  return (
    <SiteShell>
      <SeoHead
        basePath="/"
        title={c.seoHome.title}
        description={c.seoHome.description}
        lang={lang}
        includeHomeSchemas
      />

      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 80% 55% at 75% -15%, rgba(245,158,11,0.14), transparent 55%)",
          }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-6xl px-4 pb-14 pt-16 sm:px-6 sm:pb-16 sm:pt-20">
          <p className="reveal font-display text-sm font-semibold tracking-[0.08em] text-[var(--a-primary)]">
            IT-24.pro
          </p>
          <h1 className="reveal reveal-delay-1 font-display mt-4 max-w-[18ch] text-[2.35rem] font-semibold leading-[1.08] tracking-tight text-[var(--a-fg)] sm:text-5xl lg:text-[3.25rem]">
            {c.hero.h1}
          </h1>
          <p className="reveal reveal-delay-2 mt-5 max-w-[40rem] text-base leading-relaxed text-[var(--a-muted)] sm:text-lg">
            {c.hero.lead}
          </p>
          <div className="reveal reveal-delay-3 mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <TgButton label={c.hero.ctaTg} />
            <a
              href="#products"
              className="inline-flex min-h-11 items-center justify-center rounded-xl border border-[var(--a-border)] px-6 py-3 text-sm font-semibold text-[var(--a-fg)] transition duration-200 hover:border-[var(--a-primary)]/40 hover:bg-white/[0.03]"
            >
              {c.hero.ctaOffers}
            </a>
          </div>
        </div>
      </section>

      <section id="products" className="scroll-mt-24 border-t border-[var(--a-border)] py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-[var(--a-fg)] sm:text-3xl">
            {c.offersTitle}
          </h2>
          <p className="mt-2 max-w-xl text-[var(--a-muted)]">{c.offersLead}</p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {c.offers.map((o, i) => (
              <OfferTile key={o.id} offer={o} delay={(i % 4) + 1} openLabel={openLabel} />
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[var(--a-border)] bg-[var(--a-bg-elevated)] py-12 sm:py-14">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--a-muted)]">
            {c.proofTitle.split("—")[0].trim()}
          </p>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {c.proof.slice(0, 4).map((p) => (
              <li key={p.brand} className="border-l-2 border-[var(--a-primary)]/50 pl-4">
                <div className="font-display text-sm font-semibold text-[var(--a-fg)]">{p.brand}</div>
                <p className="mt-1 text-xs leading-snug text-[var(--a-muted)]">{p.note}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-[var(--a-border)] py-14 sm:py-16">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-4 sm:flex-row sm:items-center sm:px-6">
          <p className="font-display max-w-md text-xl font-semibold tracking-tight text-[var(--a-fg)] sm:text-2xl">
            {c.nav.telegramCta}
          </p>
          <TgButton label={c.nav.telegramCta} className="shrink-0" />
        </div>
      </section>
    </SiteShell>
  );
}
